import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { 
  LayoutDashboard, 
  Wallet, 
  TrendingUp, 
  Bot, 
  BarChart3, 
  LogOut,
  Bell,
  Settings,
  Shield,
  Zap,
  History,
  Sparkles
} from 'lucide-react'
import { useAuthStore } from '../store/authStore'
import { cn } from '../lib/utils'
import { WalletConnect } from './WalletConnect'
import { ConnectionStatus } from './ConnectionStatus'
import { alertsApi } from '../lib/api'

const navigation = [
  { name: 'Dashboard', href: '/', icon: LayoutDashboard },
  { name: 'Wallets', href: '/wallets', icon: Wallet },
  { name: 'Markets', href: '/markets', icon: TrendingUp },
  { name: 'Trading Bots', href: '/bots', icon: Bot },
  { name: 'Strategies', href: '/strategies', icon: Zap },
  { name: 'Backtests', href: '/backtests', icon: History },
  { name: 'Advanced Orders', href: '/advanced-orders', icon: Shield },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'AI Assistant', href: '/ai-chat', icon: Sparkles },
  { name: 'Alerts', href: '/alerts', icon: Bell },
]

export function Layout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout, isHydrated } = useAuthStore()

  // Fetch unread alert count - only after hydration
  const { data: alertData } = useQuery({
    queryKey: ['alertCount'],
    queryFn: () => alertsApi.getAll({ limit: 1 }),
    refetchInterval: 30000, // Refetch every 30 seconds
    enabled: isHydrated, // Only run after auth store is hydrated
  })

  const unreadCount = alertData?.data?.unreadCount || 0

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname
    if (path === '/') return 'Dashboard'
    if (path.startsWith('/wallets/')) return 'Wallet Details'
    if (path === '/wallets') return 'Wallets'
    if (path.startsWith('/markets/')) return 'Market Details'
    if (path === '/markets') return 'Markets'
    if (path === '/bots') return 'Trading Bots'
    if (path.startsWith('/bots/')) return 'Bot Details'
    if (path === '/analytics') return 'Analytics'
    if (path === '/alerts') return 'Alerts'
    if (path === '/settings') return 'Settings'
    return 'Dashboard'
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sidebar */}
      <div className="fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200">
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center h-16 px-6 border-b border-gray-200">
            <TrendingUp className="w-8 h-8 text-blue-600 mr-3" />
            <span className="text-xl font-bold text-gray-900">PolyTrader</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors',
                    isActive
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-50'
                  )
                }
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.name}
                {item.name === 'Alerts' && unreadCount > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="p-4 border-t border-gray-200">
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                cn(
                  'flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors mb-2',
                  isActive
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                )
              }
            >
              <Settings className="w-5 h-5 mr-3" />
              Settings
            </NavLink>

            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <LogOut className="w-5 h-5 mr-3" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="pl-64">
        {/* Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-8">
          <h1 className="text-2xl font-semibold text-gray-900">{getPageTitle()}</h1>
          <div className="flex items-center space-x-4">
            <ConnectionStatus />
            <WalletConnect />
            <NavLink
              to="/alerts"
              className="relative p-2 text-gray-500 hover:text-gray-700 transition-colors"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold w-5 h-5 flex items-center justify-center rounded-full">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </NavLink>
          </div>
        </header>

        {/* Page content */}
        <main className="p-8">{children}</main>
      </div>
    </div>
  )
}
